// src/GroomerProductionReport.js
import React, { useState } from "react";
import { apiFetch } from "./utils/apiUtils";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from "@mui/material";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const GroomerProductionReport = () => {
  // Get the first day of the current month and today's date for default values
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  };
  
  const getToday = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportGeneratedAt, setReportGeneratedAt] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");

  const handleRunReport = () => {
    if (!startDate || !endDate) {
      setError("Please select both a start and end date.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    // Call the API endpoint
    apiFetch(`/api/reports/groomer-production?start_date=${startDate}&end_date=${endDate}`)
      .then((res) => {
        if (!res.ok) {
          return res.text().then(text => { 
            throw new Error("Failed to fetch report data: " + text);
          });
        }
        return res.json();
      })
      .then((data) => {
        setReportData(data);
        setReportGeneratedAt(new Date());
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error running report:", err);
        setError("Error running report: " + err.message);
        setLoading(false);
      });
  };

  // Handle column sorting
  const handleSort = (column) => {
    // If clicking the same column, toggle direction
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new column, set it as the sort column with desc direction
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  // Add Days_Scheduled property to each row for sorting
  const processDataForSorting = (data) => {
    if (!data) return data;
    
    return data.map(row => ({
      ...row,
      Days_Scheduled: row.Groomer_Name === 'GRAND TOTAL' ? 0 : countWorkingDays(row.schedule)
    }));
  };

  // Get sorted data based on current sort column and direction
  const getSortedData = () => {
    if (!reportData || !sortColumn) return reportData;
    
    // Create a copy of the data for sorting with Days_Scheduled property, excluding grand total
    const dataToSort = [...processDataForSorting(reportData).filter(row => row.Groomer_Name !== "GRAND TOTAL")];
    const grandTotalRow = reportData.find(row => row.Groomer_Name === "GRAND TOTAL");
    
    // Sort the data
    dataToSort.sort((a, b) => {
      let valueA = a[sortColumn];
      let valueB = b[sortColumn];
      
      // Handle string comparison for Groomer_Name
      if (sortColumn === "Groomer_Name") {
        valueA = String(valueA).toLowerCase();
        valueB = String(valueB).toLowerCase();
        return sortDirection === "asc" 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } 
      // Handle numeric values
      else {
        // Convert to numbers for comparison
        valueA = Number(valueA) || 0;
        valueB = Number(valueB) || 0;
        return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
      }
    });
    
    // Add grand total row back at the end if it exists
    return grandTotalRow ? [...dataToSort, grandTotalRow] : dataToSort;
  };

  // Format currency values
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    }).format(value);
  };

  // Format number values
  const formatNumber = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };
  
  // Count working days from schedule JSON string
  const countWorkingDays = (scheduleJson) => {
    if (!scheduleJson) return "-";
    
    try {
      const schedule = JSON.parse(scheduleJson);
      let count = 0;
      
      // Count days that have any value (indicating work day)
      for (const day in schedule) {
        if (schedule[day]) {
          count++;
        }
      }
      
      return count;
    } catch (error) {
      console.error("Error parsing schedule JSON:", error);
      return "-";
    }
  };
  
  // Get sorted data
  const sortedData = reportData ? getSortedData() : null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Groomer Production Report
      </Typography>
      
      {/* Report criteria form */}
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 400, mb: 2 }}
      >
        <TextField
          label="Start Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <TextField
          label="End Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        
        <Button 
          variant="contained" 
          onClick={handleRunReport}
          disabled={loading}
        >
          {loading ? "Loading..." : "Run Report"}
        </Button>
      </Box>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Report header with title and generation timestamp */}
      {reportGeneratedAt && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Groomer Production Report</Typography>
          <Typography variant="caption">
            Report period: {startDate} to {endDate} | Generated: {reportGeneratedAt.toLocaleString()}
            <br />
            Click on column headers to dynamically sort the columns.
            <br />
            NOTE: Groom Price is split 50/50 with groomer.
            <br />
            Net Retained is 50% of groom price and the mobile fee added together. 
          </Typography>
        </Box>
      )}
      
      {/* Report results display */}
      {reportData && reportData.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto', maxWidth: '100%' }}>
            <Table size="small" sx={{ 
              '& .MuiTableCell-root': { 
                fontSize: '0.70rem', 
                padding: '4px 4px',
                whiteSpace: 'nowrap'
              }
            }}>
              <TableHead>
                <TableRow sx={{ fontWeight: 'bold', '& th': { fontWeight: 'bold' } }}>
                  <TableCell 
                    sx={{ 
                      width: 20,
                      minWidth: 20,
                      maxWidth: 20,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                    onClick={() => handleSort("Groomer_Name")}
                  >
                    Groomer Name
                    {sortColumn === "Groomer_Name" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  {/* Days Scheduled column removed - now displayed with groomer name */}
                  <TableCell
                    align="right" 
                    sx={{ 
                      width: 60,
                      minWidth: 60,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Total_Service_Price")}
                  >
                    Total Service
                    {sortColumn === "Total_Service_Price" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 60, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Num_Appts")}
                  >
                    Appts
                    {sortColumn === "Num_Appts" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 60, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Num_Pets")}
                  >
                    Pets
                    {sortColumn === "Num_Pets" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 70, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Mobile_Fee")}
                  >
                    Mobile Fee
                    {sortColumn === "Mobile_Fee" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 80, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Groom_Price")}
                  >
                    Groom Price
                    {sortColumn === "Groom_Price" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 80, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Net_Retained")}
                  >
                    Net Retained
                    {sortColumn === "Net_Retained" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 70, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Avg_Service_Price")}
                  >
                    Avg Price
                    {sortColumn === "Avg_Service_Price" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 50, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Weight_Less_10_lbs")}
                  >
                    &lt;10 lbs
                    {sortColumn === "Weight_Less_10_lbs" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 50, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Weight_10_19_lbs")}
                  >
                    10-19
                    {sortColumn === "Weight_10_19_lbs" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 50, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Weight_20_39_lbs")}
                  >
                    20-39
                    {sortColumn === "Weight_20_39_lbs" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 50, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Weight_40_59_lbs")}
                  >
                    40-59
                    {sortColumn === "Weight_40_59_lbs" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 50, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Weight_60_79_lbs")}
                  >
                    60-79
                    {sortColumn === "Weight_60_79_lbs" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 50, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Weight_80_99_lbs")}
                  >
                    80-99
                    {sortColumn === "Weight_80_99_lbs" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 50, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Over_100_Lbs")}
                  >
                    100+
                    {sortColumn === "Over_100_Lbs" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 50, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Num_Cats")}
                  >
                    Cats
                    {sortColumn === "Num_Cats" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 60, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Num_Pawdicure")}
                  >
                    Pawdicure
                    {sortColumn === "Num_Pawdicure" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      width: 60, 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={() => handleSort("Num_Cancels")}
                  >
                    Cancels
                    {sortColumn === "Num_Cancels" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData && sortedData.map((row, idx) => (
                  <TableRow 
                    key={idx} 
                    sx={{ 
                      backgroundColor: row.Groomer_Name === 'GRAND TOTAL' ? '#f5f5f5' : 'inherit',
                      fontWeight: row.Groomer_Name === 'GRAND TOTAL' ? 'bold' : 'normal',
                      '& td': { 
                        fontWeight: row.Groomer_Name === 'GRAND TOTAL' ? 'bold' : 'normal'
                      }
                    }}
                  >
                    <TableCell component="th" scope="row" sx={{ fontWeight: row.Groomer_Name === 'GRAND TOTAL' ? 'bold' : 'normal' }}>
                      {row.Groomer_Name}
                      {row.Groomer_Name !== 'GRAND TOTAL' && countWorkingDays(row.schedule) !== '-' && (
                        <Typography 
                          component="span" 
                          sx={{ 
                            fontSize: '0.65rem', 
                            opacity: 0.7, 
                            fontWeight: 'normal',
                            ml: 0.5
                          }}
                        >
                          (Works {countWorkingDays(row.schedule)} days)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.Total_Service_Price)}</TableCell>
                    <TableCell align="right">{formatNumber(row.Num_Appts)}</TableCell>
                    <TableCell align="right">{formatNumber(row.Num_Pets)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.Mobile_Fee)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.Groom_Price)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.Net_Retained)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.Avg_Service_Price)}</TableCell>
                    <TableCell align="right">{formatNumber(row.Weight_Less_10_lbs)}</TableCell>
                    <TableCell align="right">{formatNumber(row.Weight_10_19_lbs)}</TableCell>
                    <TableCell align="right">{formatNumber(row.Weight_20_39_lbs)}</TableCell>
                    <TableCell align="right">{formatNumber(row.Weight_40_59_lbs)}</TableCell>
                    <TableCell align="right">{formatNumber(row.Weight_60_79_lbs)}</TableCell>
                    <TableCell align="right">{formatNumber(row.Weight_80_99_lbs)}</TableCell>
                    <TableCell align="right">{formatNumber(row.Over_100_Lbs)}</TableCell>
                    <TableCell align="right">{formatNumber(row.Num_Cats)}</TableCell>
                    <TableCell align="center">{formatNumber(row.Num_Pawdicure)}</TableCell>
                    <TableCell align="left">{formatNumber(row.Num_Cancels)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </Box>
      ) : reportGeneratedAt && (
        <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}>
          No production data found for the selected date range.
        </Typography>
      )}
    </Box>
  );
};

export default GroomerProductionReport;